import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ShoppingBag, Loader2, ArrowLeft, Minus, Plus, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { loadAffirm } from "../lib/affirm";
import { useCart } from "../context/CartContext";
import { trackInitiateCheckout } from "../lib/metaPixel";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

const emptyAddr = { name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", postal: "" };

export default function Checkout() {
  const { items, subtotal, updateQty, removeItem, clear } = useCart();
  const navigate = useNavigate();
  const [method, setMethod] = useState("card"); // card | affirm
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState(emptyAddr);
  // Meta (Instagram/Facebook) shop link lands here as /checkout?products=<id>:<qty>,...
  // Products have multiple variants at different prices, so we can't checkout blindly —
  // send the shopper to the product page to pick the exact condition/color/storage (correct SKU + price).
  const [hydrating, setHydrating] = useState(
    () => new URLSearchParams(window.location.search).has("products")
  );
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const productsParam = params.get("products");
    if (!productsParam) return;
    hydratedRef.current = true;
    const ids = productsParam
      .split(",")
      .map((s) => s.split(":")[0].trim())
      .filter(Boolean);
    if (ids.length === 1) {
      navigate(`/product/${ids[0]}`, { replace: true });
    } else if (ids.length > 1) {
      navigate("/products", { replace: true });
    } else {
      window.history.replaceState({}, "", "/checkout");
      setHydrating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apiItems = items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id || null, quantity: i.quantity }));

  // Fire Meta Pixel InitiateCheckout once the cart is settled (incl. Meta-hydrated carts).
  const icTrackedRef = useRef(false);
  useEffect(() => {
    if (hydrating || icTrackedRef.current || items.length === 0) return;
    icTrackedRef.current = true;
    trackInitiateCheckout(apiItems, subtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrating, items.length]);

  const set = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));
  const addrValid =
    addr.name.trim() && addr.email.trim() && addr.line1.trim() &&
    addr.city.trim() && addr.state.trim() && addr.postal.trim();

  const payWithCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-cart", {
        items: apiItems,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  const payWithAffirm = async (e) => {
    e.preventDefault();
    if (!addrValid) { toast.error("Please fill in all required fields."); return; }
    setBusy(true);
    try {
      const affirm = await loadAffirm();
      const { data } = await api.post("/affirm/checkout-cart", {
        items: apiItems,
        origin_url: window.location.origin,
        shipping: { ...addr, country: "US" },
      });
      const { checkout, order_id } = data;
      affirm.checkout(checkout);
      affirm.checkout.open({
        onFail: () => { toast.message("Affirm checkout was closed."); setBusy(false); },
        onValidationError: () => { toast.error("There was a problem opening Affirm. Please try again."); setBusy(false); },
        onSuccess: async (res) => {
          try {
            await api.post("/affirm/authorize", { order_id, checkout_token: res.checkout_token });
            clear();
            window.location.assign(`/payment/success?affirm_order=${order_id}`);
          } catch {
            toast.error("We couldn't finalize your Affirm payment. If you were charged, please contact us.");
            setBusy(false);
          }
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start Affirm checkout. Please try again.");
      setBusy(false);
    }
  };

  if (hydrating) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-28" data-testid="checkout-hydrating">
        <Loader2 className="h-10 w-10 animate-spin text-crimson" />
        <p className="font-display text-xl font-bold text-ink">Taking you to the phone…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-28" data-testid="checkout-empty">
        <ShoppingBag className="h-12 w-12 text-line" />
        <p className="font-display text-2xl font-bold text-ink">Your cart is empty</p>
        <button onClick={() => navigate("/products")} className="rounded-full bg-crimson px-6 py-3 font-bold text-white hover:bg-crimson-hover">
          Browse iPhones
        </button>
      </div>
    );
  }

  const field = (key, label, props = {}) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`co-${key}`} className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</label>
      <input id={`co-${key}`} data-testid={`checkout-field-${key}`} value={addr[key]} onChange={set(key)}
        className="rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink outline-none transition-colors focus:border-crimson" {...props} />
    </div>
  );

  return (
    <div className="pt-28" data-testid="checkout-page">
      <div className="mx-auto max-w-6xl px-6 pb-24 lg:px-12">
        <button onClick={() => navigate(-1)} data-testid="checkout-back" className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-crimson">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-4xl font-black tracking-tighter text-ink sm:text-5xl">Checkout</h1>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
          {/* LINE ITEMS */}
          <div>
            <div className="divide-y divide-line rounded-2xl border border-line bg-white px-5" data-testid="checkout-items">
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 py-5" data-testid={`checkout-item-${i.key}`}>
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-line bg-alt">
                    {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" />}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold text-ink">{i.name}</div>
                        <div className="text-xs text-muted-foreground">{[i.condition, i.color, i.storage].filter(Boolean).join(" · ")}</div>
                        {i.sku && <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">SKU: {i.sku}</div>}
                      </div>
                      <button onClick={() => removeItem(i.key)} data-testid={`checkout-remove-${i.key}`} aria-label="Remove" className="text-muted-foreground hover:text-crimson">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(i.key, i.quantity - 1)} data-testid={`checkout-dec-${i.key}`} aria-label="Decrease" className="flex h-7 w-7 items-center justify-center rounded-lg border border-line hover:border-ink"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-6 text-center text-sm font-bold text-ink">{i.quantity}</span>
                        <button onClick={() => updateQty(i.key, i.quantity + 1)} disabled={i.max_qty != null && i.quantity >= i.max_qty} data-testid={`checkout-inc-${i.key}`} aria-label="Increase" className="flex h-7 w-7 items-center justify-center rounded-lg border border-line hover:border-ink disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="font-display text-base font-black text-ink">${(i.price * i.quantity).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SUMMARY + PAYMENT */}
          <div>
            <div className="rounded-2xl border border-line bg-white p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-muted-foreground">Subtotal</span>
                <span className="font-display text-2xl font-black text-ink" data-testid="checkout-subtotal">${subtotal.toLocaleString()}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Arkansas sales tax and any promo codes are applied at the final step. Free UPS shipping.</p>

              <div className="mt-6 space-y-2">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Payment method</span>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setMethod("card")} data-testid="checkout-method-card"
                    className={`rounded-xl border px-3 py-3 text-sm font-bold transition-colors ${method === "card" ? "border-crimson bg-crimson/5 text-crimson" : "border-line text-ink hover:border-ink"}`}>
                    Buy outright
                  </button>
                  <button onClick={() => setMethod("affirm")} data-testid="checkout-method-affirm"
                    className={`rounded-xl border px-3 py-3 text-sm font-bold transition-colors ${method === "affirm" ? "border-crimson bg-crimson/5 text-crimson" : "border-line text-ink hover:border-ink"}`}>
                    Pay over time
                  </button>
                </div>
              </div>

              {method === "card" ? (
                <button onClick={payWithCard} disabled={busy} data-testid="checkout-pay-card"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-crimson disabled:opacity-70">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <><ShoppingBag className="h-4 w-4" /> Pay with card</>}
                </button>
              ) : (
                <form onSubmit={payWithAffirm} className="mt-5 space-y-4" data-testid="checkout-affirm-form">
                  <p className="text-xs text-muted-foreground">Eligible customers may qualify for <span className="font-bold text-ink">0% APR</span>. Enter your shipping address to see your options — checking won't affect your credit.</p>
                  {field("name", "Full name", { placeholder: "Jane Doe", autoComplete: "name" })}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {field("email", "Email", { type: "email", placeholder: "jane@example.com", autoComplete: "email" })}
                    {field("phone", "Phone", { type: "tel", placeholder: "(555) 555-1234", autoComplete: "tel" })}
                  </div>
                  {field("line1", "Address", { placeholder: "123 Main St", autoComplete: "address-line1" })}
                  {field("line2", "Apt / Suite (optional)", { placeholder: "Apt 4B", autoComplete: "address-line2" })}
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {field("city", "City", { placeholder: "Little Rock", autoComplete: "address-level2" })}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="co-state" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">State</label>
                      <select id="co-state" data-testid="checkout-field-state" value={addr.state} onChange={set("state")}
                        className="rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink outline-none transition-colors focus:border-crimson">
                        <option value="">—</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {field("postal", "ZIP", { placeholder: "72201", autoComplete: "postal-code" })}
                  </div>
                  <button type="submit" disabled={busy} data-testid="checkout-pay-affirm"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-crimson disabled:opacity-70">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening Affirm…</> : <><CreditCard className="h-4 w-4" /> Continue with Affirm</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
